<script setup lang="ts">
  import useTaboolaAds from '../../composables/ad-ops/useTaboolaAds'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'

  /**
   * Props for the Taboola component
   * @property containerName - The name of the container for the ad
   * @property placement - The placement identifier for the ad
   * @property mode - style of the ad
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * @property visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   */
  export interface TaboolaProps {
    containerName: string
    widgetId: string
    placement: string
    mode: string
    tilePosition?: number
    adHeight?: number
    adWidth?: number
    visibilityThresholdInPercentage?: number
    visibilityTimeoutInMs?: number
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<TaboolaProps>(), {
    tilePosition: -1,
    adHeight: 304,
    adWidth: 300,
    visibilityThresholdInPercentage: 0, // 0 means no threshold, always trigger callback
    visibilityTimeoutInMs: 0, // 0 means no timeout
    adInstanceId: '',
  })

  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')

  const { renderWhenCurrentElementIsVisible, adClicked } = useAdFunctions()

  const { triggerTaboolaRenderedEvent, fireRenderCallback } = useTaboolaAds()

  const { track } = useAnalytics()

  const containerName = ref<string>(props.containerName)
  const widgetName = ref<string>(props.placement)
  const placementClassName = ref<string>(props.placement)

  const adBrand = ref<string>('')
  const setAdBrand = (element: Element) => {
    const item =
      element?.querySelector('.branding-inner') ??
      element?.querySelector('.branding')
    adBrand.value =
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (item as HTMLElement)?.innerText || item?.textContent || ''
  }

  const renderTaboolaElement = ref(false)

  const getEventData = (): NtpAdEvent => ({
    ntp_ad_brand: adBrand.value,
    ntp_tile_position: props.tilePosition,
    ntp_ad_provider: 'taboola',
    ntp_creative_id: 'taboola-' + props.placement,
    ntp_placement_name: props.placement,
  })

  const handleAdClick = () => {
    adClicked(getEventData(), props.placement)
  }

  const attachClickEventToAnchorTag = (element: Element) => {
    const anchorTag = element.querySelector('a')
    if (anchorTag && !anchorTag.getAttribute('data-click-registered')) {
      anchorTag.setAttribute('data-click-registered', 'true')
      anchorTag.addEventListener('click', handleAdClick)
    }
  }

  const detachClickEventFromAnchorTags = (element: Element) => {
    const registeredAnchors = element.querySelectorAll(
      'a[data-click-registered="true"]'
    )
    registeredAnchors.forEach((anchorTag) => {
      anchorTag.removeEventListener('click', handleAdClick)
      anchorTag.removeAttribute('data-click-registered')
    })
  }

  const postRenderCallbacks = () => {
    if (!widgetWrapperEl.value) return

    attachClickEventToAnchorTag(widgetWrapperEl.value)

    track('NtpAd_Rendered', getEventData())
  }

  const handleTaboolaRenderedEvent = () => {
    const executed = ref<boolean>(false)
    if (!executed.value) {
      if (widgetWrapperEl.value) {
        setAdBrand(widgetWrapperEl.value)
      }
      fireRenderCallback(props.placement, executed, postRenderCallbacks)
    }
  }

  const renderTaboolaAd = () => {
    renderTaboolaElement.value = true

    nextTick(() => {
      if (
        !widgetWrapperEl.value ||
        widgetWrapperEl.value?.getAttribute('data-render-registered')
      )
        return

      const index = getIndexOfPlacement(props.placement, widgetWrapperEl.value)
      if (index > 0) {
        // for taboola properties endless starts at 4 since other units take <4
        const taboolaStartIndex = 4
        widgetName.value = widgetName.value + (index + taboolaStartIndex)
        containerName.value = props.containerName + (index + taboolaStartIndex)
        // class name we want the instance of the ad (not the same as taboola)
        placementClassName.value =
          index > 0
            ? `${props.placement} ${props.placement}-${index}`
            : props.placement
      }

      triggerTaboolaRenderedEvent(
        props.mode,
        containerName.value,
        props.placement,
        handleTaboolaRenderedEvent
      )
    })
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      widgetWrapperEl.value,
      props.visibilityTimeoutInMs,
      props.visibilityThresholdInPercentage,
      renderTaboolaAd
    )
  })

  onUnmounted(() => {
    if (widgetWrapperEl.value) {
      detachClickEventFromAnchorTags(widgetWrapperEl.value)
    }
  })
</script>
<template>
  <div
    ref="widgetWrapperEl"
    :key="props.adInstanceId"
    :class="[placementClassName, widgetName]"
    :style="{
      width: `${props.adWidth}px`,
      height: `${props.adHeight}px`,
    }"
  >
    <div
      v-if="renderTaboolaElement"
      :id="containerName"
      class="taboola-homepage-ad"
      :data-ad-brand="adBrand"
      :style="{
        width: `${props.adWidth}px`,
        height: `${props.adHeight}px`,
      }"
    />
  </div>
</template>
